import { Alert, AlertTitle, Box, Button } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Clear as ClearIcon } from '@mui/icons-material';
import { ValidationError } from '@/lib/validationErrors';
import { useTranslation } from 'react-i18next';

interface ValidationErrorBannerProps {
  /** Array of validation errors */
  errors: ValidationError[];
  /** Callback when expand button is clicked */
  onExpand: () => void;
  /** Callback when clear button is clicked */
  onClear: () => void;
}

/**
 * Validation Error Banner
 * Displays a summary of validation errors at the top of forms
 * Includes expand and clear buttons
 */
export function ValidationErrorBanner({ errors, onExpand, onClear }: ValidationErrorBannerProps) {
  const { t } = useTranslation();

  if (!errors || errors.length === 0) {
    return null;
  }

  const errorCount = errors.length;
  const firstError = errors[0];
  
  // For single errors or business rule errors (mapped to "form" field), show the actual message
  const isBusinessRuleError = firstError.field === 'form';
  const showMessage = errorCount === 1 || isBusinessRuleError;

  return (
    <Alert 
      severity="error" 
      sx={{ 
        borderRadius: 0,
        '& .MuiAlert-action': {
          alignItems: 'center',
          gap: 1
        }
      }}
      action={
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            size="small"
            color="inherit"
            onClick={onExpand}
            startIcon={<ExpandMoreIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {t('validation.expand')}
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={onClear}
            startIcon={<ClearIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {t('validation.clear')}
          </Button>
        </Box>
      }
    >
      {showMessage ? (
        <>
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
            {firstError.message}
          </AlertTitle>
          {errorCount > 1 && (
            <Box sx={{ fontSize: '0.875rem', mt: 0.5 }}>
              {t('validation.errorsFoundExpand', { count: errorCount })}
            </Box>
          )}
        </>
      ) : (
        <>
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('validation.failed')}
          </AlertTitle>
          {t('validation.errorsFoundExpandOrClear', { count: errorCount })}
        </>
      )}
    </Alert>
  );
}

